import React, { FC } from 'react'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'

import {
  legalLinks,
  companyLinks,
  productLinks,
  serviceLinks,
  customerCareLinks,
  enterpriseSystemLinks,
  regionLinks,
} from './Header'
import ActiveLink from './ActiveLink'
import Ratings from 'components/Ratings'
import Strings from 'project/localisation'
import Copyright from 'components/Copyright'
import Accordion from 'components/Accordion'
import Button from 'components/forms/Button'
import Card from 'components/cards/base/Card'
import dynamic from 'next/dynamic'
import { TLinkGroup } from 'common/cms-types'

// Dynamic so next-share (+ jsonp/debug deps) code-splits out of the _app
// chunk — the footer puts this component on every page.
const SocialLinks = dynamic(() => import('components/SocialLinks'))
import { generateSrcSet } from 'project/image-proxy-web'
import CompanyInformationCard from 'components/cards/CompanyInformationCard'
import { GooglePlayButton, AppStoreButton } from 'components/AppDownloadButtons'
import CookiePreferencesLink from './CookiePreferencesLink'
import { Tenant } from 'common/tenant'

function mobileFooterLinks(au: boolean) {
  if (au) {
    return [
      {
        links: enterpriseSystemLinks(au),
        title: Strings.enterpriseSystems,
      },

      {
        links: legalLinks(au),
        title: Strings.legalInformation,
      },
      {
        links: regionLinks(),
        title: Strings.region,
      },
    ] as TLinkGroup[]
  }
  return [
    {
      links: productLinks(),
      title: Strings.products,
    },
    {
      links: enterpriseSystemLinks(au),
      title: Strings.enterpriseSystems,
    },
    {
      links: customerCareLinks(),
      title: Strings.customerCare,
    },
    {
      links: legalLinks(au),
      title: Strings.legalInformation,
    },
    {
      links: regionLinks(),
      title: Strings.region,
    },
  ] as TLinkGroup[]
}

function _footerLinks(au) {
  if (au) {
    return [
      [
        {
          links: enterpriseSystemLinks(au),
          title: Strings.enterpriseSystems,
        },
      ],
      [
        {
          body: (
            <div className='mt-4'>
              <CompanyInformationCard tenant={au ? Tenant.AU : Tenant.UK} />
            </div>
          ),
          title: Strings.needHelp,
        },
      ],
      [
        {
          links: legalLinks(au),
          title: Strings.legalInformation,
        },
      ],
      [
        {
          body: <SocialLinks />,

          title: Strings.followUs,
        },
        {
          links: regionLinks(),
          title: Strings.region,
        },
      ],
    ] as TLinkGroup[][]
  }
  return [
    [
      {
        links: productLinks(),
        title: Strings.products,
      },
      {
        links: serviceLinks(),
        title: Strings.services,
      },
    ],
    [
      {
        links: enterpriseSystemLinks(au),
        title: Strings.enterpriseSystems,
      },
    ],
    [
      {
        links: companyLinks(),
        title: Strings.company,
      },
    ],

    [
      {
        links: customerCareLinks(),
        title: Strings.customerCare,
      },
      {
        links: regionLinks(),
        title: Strings.region,
      },
    ],
    [
      {
        links: legalLinks(au),
        title: Strings.legalInformation,
      },
    ],
  ] as TLinkGroup[][]
}

type FooterType = {
  hideFooterCard?: boolean
  tenant?: Tenant
}

const Footer: FC<FooterType> = ({ hideFooterCard, tenant = Tenant.UK }) => {
  const au = tenant === Tenant.AU
  const footerLinks = _footerLinks(au)
  const router = useRouter()
  const currentPath = router.asPath.replace(/\//g, '')

  return (
    <footer className={hideFooterCard ? 'pt-8' : ''}>
      <div className='d-flex justify-content-center p-5 footer-card'>
        {!hideFooterCard && (
          <Card
            shadow
            className='footer-card shadow-2 overflow-hidden justify-content-center'
          >
            <div className='d-flex flex-column flex-md-row h-100 align-items-md-end bg-white'>
              <div className='px-8 ps-md-0'>
                <img
                  {...generateSrcSet(
                    au
                      ? '/images/new/au/robot-upper-half.png'
                      : '/images/new/robot-upper-half.png',
                    409,
                    306,
                  )}
                  loading='lazy'
                  className='img-fluid d-none d-md-block mx-auto me-lg-n4'
                  height={306}
                  width={409}
                  alt={'Hoxton Mix Illustration'}
                />
              </div>
              <div className='mb-6 d-flex text-center text-md-start flex-column footer-card-content ps-lg-10 pe-lg-8 px-8'>
                <div className='flex-fill mt-6 mt-md-0 px-10 px-md-8 mb-lg-7'>
                  <h3 className='h4 mb-2'>{Strings.weHaveHelped}</h3>
                  <div className='h4 text-muted mb-md-8 mb-0'>
                    {Strings.letUsHelp}
                  </div>
                </div>

                <img
                  {...generateSrcSet(
                    '/images/new/robot-holding-mail.png',
                    283,
                    342,
                  )}
                  loading='lazy'
                  className='img-fluid d-block d-md-none mx-auto me-lg-n4'
                  width={283}
                  height={342}
                  alt={'Hoxton Mix Illustration'}
                />
                {!au &&
                  currentPath !== 'checkout' &&
                  currentPath !== 'thanks' &&
                  currentPath !== 'payment' &&
                  currentPath !== 'konnect' && (
                    <div className='w-100 d-md-flex justify-content-lg-end justify-content-center'>
                      <Link
                        className='a-unstyled'
                        href={'/virtual-office-london-old-street'}
                      >
                        <Button
                          className={'px-12 w-100'}
                          size='medium'
                          theme='secondary'
                        >
                          Get Started
                        </Button>
                      </Link>
                    </div>
                  )}
                {!!au && (
                  <div className='w-100 d-md-flex justify-content-lg-end justify-content-center'>
                    <Link
                      className='a-unstyled'
                      href={'/au/enterprise#konnect-form'}
                    >
                      <Button
                        className={'px-12 w-100'}
                        size='medium'
                        theme='secondary'
                      >
                        Request a Demo
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>

      <div className='footer-container d-lg-none'>
        <Accordion
          chevronColour='#6E6D72'
          items={mobileFooterLinks(au).map((v) => ({
            body: (
              <>
                {v.links.map((link) => {
                  const LinkTag = link.reload ? 'a' : Link
                  return (
                    <div key={link.title}>
                      <LinkTag
                        className='a-unstyled'
                        href={link.href}
                        target={link.target}
                        rel={
                          link.target === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                        }
                      >
                        <span className='link cursor-pointer'>
                          {link.title}
                        </span>
                      </LinkTag>
                    </div>
                  )
                })}
                {v.title === Strings.legalInformation && (
                  <div>
                    <CookiePreferencesLink className='a-unstyled link cursor-pointer' />
                  </div>
                )}
              </>
            ),
            title: `${v.title}`,
          }))}
        />
        <div size={5} className={cn('h6 fw-bold mt-6')}>
          {Strings.companyInformation}
        </div>
        <CompanyInformationCard tenant={au ? Tenant.AU : Tenant.UK} />
        <SocialLinks linkType='social' />
        <hr />
        <Ratings />
        <hr />
        <Copyright tenant={au ? Tenant.AU : Tenant.UK} />
        <div className='d-flex justify-content-center gap-3'>
          <AppStoreButton />
          <GooglePlayButton />
        </div>
      </div>
      <div className='footer-container d-none d-lg-block'>
        <div className='container'>
          <div className='row justify-content-between footer__links'>
            {footerLinks.map((data, i) => (
              <div
                className={footerLinks?.length < 5 ? 'col-md-3' : 'col-md-2'}
                key={i}
              >
                <>
                  {data.map((list) => (
                    <>
                      <div className='list-footer' key={list.title}>
                        <p className='h6 mt-15'>{list.title}</p>
                        {list.body || (
                          <>
                            {list.links.map((data, i) => {
                              const isAbsolute =
                                data.href.includes('http') || data.reload
                              const Tag = isAbsolute ? `a` : ActiveLink
                              return (
                                <Tag
                                  href={data.href}
                                  className='d-block nav-link'
                                  rel={
                                    data.target === '_blank'
                                      ? 'noopener noreferrer'
                                      : 'noreferrer'
                                  }
                                  target={data.target}
                                  prefetch={false}
                                  key={i}
                                >
                                  <>{data.title}</>
                                </Tag>
                              )
                            })}
                            {list.title === Strings.legalInformation && (
                              <CookiePreferencesLink className='d-block nav-link cursor-pointer' />
                            )}
                          </>
                        )}
                      </div>
                    </>
                  ))}
                </>

                {footerLinks.length - 1 === i && !au && (
                  <div className='mt-8'>
                    <div size={5} className={cn('h6 fw-bold')}>
                      {Strings.companyInformation}
                    </div>
                    <CompanyInformationCard />
                    <SocialLinks linkType='social' />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className='col-md-8 offset-md-2'>
          <Ratings />
          <hr />
          <Copyright tenant={au ? Tenant.AU : Tenant.UK} />
        </div>
      </div>
    </footer>
  )
}

export default Footer
